import React from "react";
import {
  Field,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
  RadioGroup,
  Radio,
  Slider,
} from "@fluentui/react-components";
import DataValidationMessageBar from "../../../shared/components/DataValidationMessageBar";
import { GenericComboBox } from "../../../shared/components/GenericComboBox";

const TicketEditorProperties = ({
  validationErrors,
  ticketStatusesData,
  CurrentTicket,
  updateField,
  agentsData,
  impactOptions,
  priorityOptions,
  categoriesData,
  subcategoriesData,
  itemsData,
  openItems,
  setUserToggledAccordions,
  setOpenItems,
  accordionRef,
  ticketSourcesData,
  usersData,
  sitesData,
  isCreating,
  isUpdating,
  updateTicketMutate,
  createTicketMutate,
  handleClose,
  t,
  styles,
}) => (
  <aside style={{ display: "flex", flex: "0 0 350px", flexDirection: "column", height: "100%" }}>
    <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
      {validationErrors && Object.keys(validationErrors).length > 0 && (
        <DataValidationMessageBar style={{ margin: "0 12px 12px 12px" }} validationErrors={validationErrors} />
      )}
      <div style={{ padding: "8px 12px", display: "flex", flexDirection: "column", gap: "8px" }}>
        <Field
          id="status"
          className={styles.field}
          label={t("generic.status")}
          validationState={validationErrors.status_id ? "error" : undefined}
        >
          <GenericComboBox
            items={ticketStatusesData}
            value={CurrentTicket?.status_id ?? null}
            valueKey="id"
            displayKey="name"
            onOptionSelect={(newStatusId) => updateField("status_id", newStatusId)}
          />
        </Field>
        <Field
          id="agent"
          className={styles.field}
          label={t("generic.agent")}
          validationState={validationErrors.agent_id ? "error" : undefined}
        >
          <GenericComboBox
            items={agentsData}
            value={CurrentTicket?.agent_id ?? null}
            valueKey="id"
            displayKey="name"
            onOptionSelect={(newAgentId) => updateField("agent_id", newAgentId)}
          />
        </Field>
      </div>
      <div style={{ padding: "12px" }}>
        <div style={{ display: "flex", columnGap: "16px", flexWrap: "wrap" }}>
          <Field
            id="impact"
            className={styles.field}
            label={
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <span
                  style={{
                    width: "12px",
                    height: "12px",
                    borderRadius: "50%",
                    backgroundColor: impactOptions.find((opt) => opt.id === CurrentTicket?.impact)?.color || "#107C10",
                    display: "inline-block",
                  }}
                />
                {`${t("generic.impact")} - ${
                  impactOptions.find((opt) => opt.id === CurrentTicket?.impact)?.label || "Low"
                }`}
              </div>
            }
            validationState={validationErrors.impact ? "error" : undefined}
            style={{ flex: 1 }}
          >
            <Slider
              value={Math.max(
                impactOptions.findIndex((opt) => opt.id === CurrentTicket?.impact),
                0,
              )}
              min={0}
              max={impactOptions.length - 1}
              step={1}
              onChange={(_, data) => updateField("impact", impactOptions[data.value]?.id)}
              style={{
                "--slider-background": "linear-gradient(to right, #107C10 0%, #FF8C00 50%, #D13438 100%)",
              }}
            />
          </Field>
          <Field
            id="urgency"
            className={styles.field}
            label={
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <span
                  style={{
                    width: "12px",
                    height: "12px",
                    borderRadius: "50%",
                    backgroundColor: impactOptions.find((opt) => opt.id === CurrentTicket?.urgency)?.color || "#107C10",
                    display: "inline-block",
                  }}
                />
                {`${t("generic.urgency")} - ${
                  impactOptions.find((opt) => opt.id === CurrentTicket?.urgency)?.label || "Low"
                }`}
              </div>
            }
            style={{ flex: 1 }}
          >
            <Slider
              value={Math.max(
                impactOptions.findIndex((opt) => opt.id === CurrentTicket?.urgency),
                0,
              )}
              min={0}
              max={impactOptions.length - 1}
              step={1}
              onChange={(_, data) => updateField("urgency", impactOptions[data.value]?.id)}
              style={{
                color: "#107C10",
              }}
            />
          </Field>
        </div>
        <Field
          id="priority"
          className={styles.field}
          label={
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span
                style={{
                  width: "12px",
                  height: "12px",
                  borderRadius: "50%",
                  backgroundColor:
                    priorityOptions.find((opt) => opt.id === CurrentTicket?.priority)?.color || "#107C10",
                  display: "inline-block",
                }}
              />
              {`${t("generic.priority")} - ${
                priorityOptions.find((opt) => opt.id === CurrentTicket?.priority)?.label || "Low"
              }`}
            </div>
          }
          validationState={validationErrors.priority ? "error" : undefined}
        >
          <Slider
            value={Math.max(
              priorityOptions.findIndex((opt) => opt.id === CurrentTicket?.priority),
              0,
            )}
            min={0}
            max={priorityOptions.length - 1}
            step={1}
            onChange={(_, data) => updateField("priority", priorityOptions[data.value]?.id)}
            style={{
              color: "#D13438",
            }}
          />
        </Field>
      </div>
      <div style={{ padding: "0 12px" }}>
        <div style={{ padding: "8px 0" }}>
          <RadioGroup
            value={CurrentTicket?.type ?? "INC"}
            onChange={(_, data) => updateField("type", data.value)}
            style={{ display: "flex", flexDirection: "row", gap: "16px", flexWrap: "nowrap" }}
          >
            <Radio value="INC" label="Incident" />
            <Radio value="SRQ" label="Service Request" />
          </RadioGroup>
        </div>
        <Field
          id="category"
          className={styles.field}
          label={t("generic.category")}
          validationState={validationErrors.category ? "error" : undefined}
        >
          <GenericComboBox
            items={categoriesData}
            value={CurrentTicket?.category_id ?? null}
            valueKey="id"
            displayKey="name"
            onOptionSelect={(newCategoryId) => updateField("category_id", newCategoryId)}
          />
        </Field>
        <Field
          id="subcategory"
          className={styles.field}
          label={t("generic.subcategory")}
          validationState={validationErrors.subcategory_id ? "error" : undefined}
        >
          <GenericComboBox
            disabled={!CurrentTicket?.category_id}
            items={subcategoriesData}
            value={CurrentTicket?.subcategory_id ?? null}
            valueKey="id"
            displayKey="name"
            onOptionSelect={(newSubcategoryId) => updateField("subcategory_id", newSubcategoryId)}
          />
        </Field>
        <Field
          id="item"
          className={styles.field}
          label={t("generic.item")}
          validationState={validationErrors.item_id ? "error" : undefined}
        >
          <GenericComboBox
            disabled={!CurrentTicket?.subcategory_id}
            items={itemsData}
            value={CurrentTicket?.item_id ?? null}
            valueKey="id"
            displayKey="name"
            onOptionSelect={(newItemId) => updateField("item_id", newItemId)}
          />
        </Field>
      </div>
      <Accordion
        ref={accordionRef}
        multiple
        collapsible
        openItems={openItems}
        onToggle={(_, data) => {
          setUserToggledAccordions(true);
          setOpenItems(Array.isArray(data.openItems) ? data.openItems : data.openItems ? [data.openItems] : []);
        }}
        style={{ flexGrow: 1 }}
      >
        <AccordionItem value="additionalDetails">
          <AccordionHeader>{t("generic.additionalDetails", { defaultValue: "Additional Details" })}</AccordionHeader>
          <AccordionPanel>
            <Field
              id="source"
              className={styles.field}
              label={t("generic.source")}
              validationState={validationErrors.source_id ? "error" : undefined}
            >
              <GenericComboBox
                items={ticketSourcesData}
                value={CurrentTicket?.source_id ?? null}
                valueKey="id"
                displayKey="name"
                onOptionSelect={(newSourceId) => updateField("source_id", newSourceId)}
              />
            </Field>
            <Field
              id="requestedBy"
              className={styles.field}
              label={t("nounPhrases.requestedBy")}
              validationState={validationErrors.requestedby_id ? "error" : undefined}
            >
              <GenericComboBox
                items={usersData}
                value={CurrentTicket?.requestedby_id ?? null}
                valueKey="id"
                displayKey="name"
                onOptionSelect={(newRequestedById) => updateField("requestedby_id", newRequestedById)}
              />
            </Field>
            <Field
              id="requestedFor"
              className={styles.field}
              label={t("nounPhrases.requestedFor")}
              validationState={validationErrors.requestedfor_id ? "error" : undefined}
            >
              <GenericComboBox
                items={usersData}
                value={CurrentTicket?.requestedfor_id ?? null}
                valueKey="id"
                displayKey="name"
                onOptionSelect={(newRequestedForId) => updateField("requestedfor_id", newRequestedForId)}
              />
            </Field>
            <Field
              id="site"
              className={styles.field}
              label={t("generic.site")}
              validationState={validationErrors.site_id ? "error" : undefined}
            >
              <GenericComboBox
                items={sitesData}
                value={CurrentTicket?.site_id ?? null}
                valueKey="id"
                displayKey="name"
                onOptionSelect={(newSiteId) => updateField("site_id", newSiteId)}
              />
            </Field>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </div>
  </aside>
);

export default TicketEditorProperties;
